import React, { useCallback, useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { Modal, ModalBody, ModalFooter } from "reactstrap";
import constant from "Constant";
import { Button } from "components/ui";
import { OriAvatar, ORI_ORB_CROP_SCALE } from "components/ori";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";
import styles from "./OriDisclaimerModal.module.scss";

interface OriDisclaimerModalProps {
  firstName?: string;
  onAccept: () => Promise<void>;
  onDismiss: () => void;
  isAccepting: boolean;
}

const OriDisclaimerModal: React.FC<OriDisclaimerModalProps> = ({
  firstName,
  onAccept,
  onDismiss,
  isAccepting,
}) => {
  const { t } = useTranslation(["ori", "common"]);
  const openedAt = useRef<number>(Date.now());

  useEffect(() => {
    openedAt.current = Date.now();
    trackEvent("Ori Disclaimer Viewed");
  }, []);

  const handleContinue = useCallback(async () => {
    trackEvent("Ori Disclaimer Continue Pressed", {
      "Time To Accept Ms": Date.now() - openedAt.current,
    });
    await onAccept();
  }, [onAccept]);

  const handleDismiss = useCallback(() => {
    trackEvent("Ori Disclaimer Declined");
    onDismiss();
  }, [onDismiss]);

  const userName = firstName || t("ori:disclaimer.defaultName");
  const disclaimerPoints = [
    t("ori:disclaimer.points.stillLearning"),
    t("ori:disclaimer.points.notMedical"),
    t("ori:disclaimer.points.anonymised"),
  ];

  return (
    <Modal
      isOpen
      centered
      toggle={handleDismiss}
      className={styles.modal}
      contentClassName={styles.content}
    >
      <button
        type="button"
        className={styles.closeButton}
        onClick={handleDismiss}
        aria-label={t("common:action.close")}
        data-testid="ori-disclaimer-dismiss"
      >
        {"×"}
      </button>
      <ModalBody className={styles.body} data-testid="ori-disclaimer-modal">
        <div className={styles.header}>
          <OriAvatar size={48} cropScale={ORI_ORB_CROP_SCALE} />
          <h1 className={styles.title}>
            {t("ori:disclaimer.title", { name: userName })}
          </h1>
          <p className={styles.subtitle}>{t("ori:disclaimer.subtitle")}</p>
        </div>
        <div className={styles.card}>
          <p className={styles.cardTitle}>{t("ori:disclaimer.cardTitle")}</p>
          {disclaimerPoints.map((text) => (
            <p key={text} className={styles.disclaimer}>
              {text}
            </p>
          ))}
        </div>
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <Button
          variant="enclosed-primary"
          monochromeColour="white"
          fullWidth
          loading={isAccepting}
          onClick={handleContinue}
          data-testid="ori-disclaimer-continue"
        >
          {t("common:action.continue")}
        </Button>
        <p className={styles.privacy}>
          <Trans
            i18nKey="ori:disclaimer.privacy"
            components={{
              link: (
                <a
                  href={constant.PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.privacyLink}
                  data-testid="ori-disclaimer-privacy-link"
                >
                  Privacy Policy
                </a>
              ),
            }}
          />
        </p>
      </ModalFooter>
    </Modal>
  );
};

export default OriDisclaimerModal;
